/**
 * Perfil (spec 011, secao 5; spec 038, secao 3): os dados que o proprio cliente mantem (nome, nascimento, telefone,
 * endereco e a chave PIX do repasse) e o acesso: os e-mails (adicionar com codigo e escolher o principal) e a troca de
 * senha. Nome, CPF e e-mail moram aqui (sairam do Inicio). O CPF nao muda: o CPF e a pessoa.
 * Spec 039: "Meus comprovantes", os que a pessoa mandou pela mensalidade ou pelo pedido, com o que a equipe decidiu.
 */
import {
  Aviso, Botao, Campo, CampoCodigo, CampoData, CampoSelecao, CampoSenha, Cartao, EsqueletoDeCartao, Pilha, Selo, UFS,
  emailValido, formatarCep, formatarChavePix, formatarCpf, formatarTelefone, type RespostaDeEntrada, type TipoDeChavePix,
} from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useAcao, useApi } from "../acoes";
import { useSessao } from "../sessao";
import { MeusComprovantes, useComprovantes } from "./comprovantes";

type Perfil = {
  id: string;
  cpf: string;
  nome: string;
  telefone: string | null;
  nascimento: string | null;
  endereco: { cep: string | null; logradouro: string | null; numero: string | null; complemento: string | null; bairro: string | null; cidade: string | null; uf: string | null; pais: string | null } | null;
  pix: { tipo: string | null; chave: string | null } | null;
  emails: { id: string; email: string; principal: boolean; verificado: boolean }[];
  acesso: { senha_definida_em: string | null; ultimo_login_em: string | null };
};

const TIPOS_DE_PIX: { valor: TipoDeChavePix; rotulo: string }[] = [
  { valor: "CPF", rotulo: "CPF" },
  { valor: "EMAIL", rotulo: "E-mail" },
  { valor: "TELEFONE", rotulo: "Telefone" },
  { valor: "CNPJ", rotulo: "CNPJ" },
  { valor: "ALEATORIA", rotulo: "Chave aleatória" },
];
/** Como a chave de cada tipo se escreve, pro exemplo do campo e o teclado certo no celular. */
const CHAVE_POR_TIPO: Record<TipoDeChavePix, { exemplo: string; teclado: "numeric" | "email" | "tel" | "text" }> = {
  CPF: { exemplo: "000.000.000-00", teclado: "numeric" },
  CNPJ: { exemplo: "00.000.000/0000-00", teclado: "numeric" },
  TELEFONE: { exemplo: "(11) 98765-4321", teclado: "tel" },
  EMAIL: { exemplo: "voce@exemplo.com", teclado: "email" },
  ALEATORIA: { exemplo: "0000aaaa-00aa-00aa-00aa-000000aaaaaa", teclado: "text" },
};
const dia = (iso: string | null) => (iso ? iso.slice(0, 10) : "");

export function PaginaPerfil() {
  const { ler } = useApi();
  const q = useQuery({ queryKey: ["perfil"], queryFn: () => ler<Perfil>("/v1/me") });
  // Sem mensalidade no ambiente, nao ha o que mandar (spec 039, 3.11); a secao so aparece se ja houver algum.
  const { estado } = useSessao();
  const lista = useComprovantes();
  const comprovantes = (estado.status === "logado" && estado.usuario.capacidades.modulos.includes("mensalidade-manual")) || Boolean(lista.data?.itens.length);
  const cabecalho = (
    <header className="mtc-cabecalho__textos">
      <h1 className="mtc-titulo mtc-cabecalho__titulo">Perfil</h1>
    </header>
  );
  if (q.isLoading) return <Pilha espaco={6}>{cabecalho}<EsqueletoDeCartao linhas={4} rotulo="Abrindo seu perfil" /><EsqueletoDeCartao linhas={2} /></Pilha>;
  if (q.isError || !q.data) return <Pilha espaco={6}>{cabecalho}<Aviso tipo="erro">Não consegui abrir seu perfil agora. Tente de novo em instantes.</Aviso></Pilha>;
  // A `key` e a pessoa: os campos ja nascem com o que esta salvo (sem piscar vazios) e o que voce digita nao e
  // atropelado quando a tela busca de novo.
  return (
    <Pilha espaco={6}>
      {cabecalho}
      <Formulario key={q.data.id} p={q.data} />
      <Emails p={q.data} />
      <TrocarSenha />
      {comprovantes && <MeusComprovantes />}
    </Pilha>
  );
}

function Formulario({ p }: { p: Perfil }) {
  const { por } = useApi();
  const qc = useQueryClient();
  const acao = useAcao();
  const [feito, setFeito] = useState(false);
  const [f, setF] = useState({
    nome: p.nome,
    telefone: p.telefone ? formatarTelefone(p.telefone) : "",
    nascimento: dia(p.nascimento),
    cep: p.endereco?.cep ? formatarCep(p.endereco.cep) : "",
    logradouro: p.endereco?.logradouro ?? "",
    numero: p.endereco?.numero ?? "",
    complemento: p.endereco?.complemento ?? "",
    bairro: p.endereco?.bairro ?? "",
    cidade: p.endereco?.cidade ?? "",
    uf: p.endereco?.uf ?? "",
    pais: p.endereco?.pais ?? "Brasil",
    pixTipo: p.pix?.tipo ?? "",
    pixChave: p.pix?.chave ?? "",
  });

  const salvar = async (ev: FormEvent) => {
    ev.preventDefault();
    setFeito(false);
    const ok = await acao.rodar(() =>
      por("/v1/me/perfil", {
        nome: f.nome,
        telefone: f.telefone || null,
        nascimento: f.nascimento || null,
        endereco: { cep: f.cep || null, logradouro: f.logradouro || null, numero: f.numero || null, complemento: f.complemento || null, bairro: f.bairro || null, cidade: f.cidade || null, uf: f.uf || null, pais: f.pais || null },
        pix: { tipo: f.pixChave ? f.pixTipo || null : null, chave: f.pixChave || null },
      }),
    );
    if (!ok) return;
    setFeito(true);
    await qc.invalidateQueries({ queryKey: ["perfil"] });
  };

  const tipo = (f.pixTipo || null) as TipoDeChavePix | null;
  const chave = tipo ? CHAVE_POR_TIPO[tipo] : null;
  return (
    <Cartao>
      <form className="mtc-formulario" onSubmit={salvar} noValidate>
        <Pilha espaco={4}>
          <span className="mtc-rotulo">Seus dados</span>
          {acao.erro && <Aviso tipo="erro">{acao.erro}</Aviso>}
          {feito && <Aviso tipo="sucesso">Dados salvos.</Aviso>}
          <div className="mtc-grade" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))" }}>
            <Campo rotulo="Nome completo" valor={f.nome} aoMudar={(v) => setF({ ...f, nome: v })} autoComplete="name" required />
            <Campo rotulo="CPF" valor={formatarCpf(p.cpf)} aoMudar={() => undefined} mono disabled dica="O CPF não muda: ele é a sua identidade aqui." />
            <Campo rotulo="Celular com DDD" valor={f.telefone} aoMudar={(v) => setF({ ...f, telefone: formatarTelefone(v) })} inputMode="tel" autoComplete="tel" placeholder="(11) 98765-4321" />
            <CampoData rotulo="Data de nascimento" valor={f.nascimento} aoMudar={(v) => setF({ ...f, nascimento: v })} />
          </div>
          <span className="mtc-rotulo">Endereço</span>
          <div className="mtc-grade" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))" }}>
            <Campo rotulo="CEP" valor={f.cep} aoMudar={(v) => setF({ ...f, cep: formatarCep(v) })} mono inputMode="numeric" autoComplete="postal-code" placeholder="00000-000" />
            <Campo rotulo="Rua" valor={f.logradouro} aoMudar={(v) => setF({ ...f, logradouro: v })} autoComplete="address-line1" />
            <Campo rotulo="Número" valor={f.numero} aoMudar={(v) => setF({ ...f, numero: v })} />
            <Campo rotulo="Complemento" valor={f.complemento} aoMudar={(v) => setF({ ...f, complemento: v })} autoComplete="address-line2" />
            <Campo rotulo="Bairro" valor={f.bairro} aoMudar={(v) => setF({ ...f, bairro: v })} />
            <Campo rotulo="Cidade" valor={f.cidade} aoMudar={(v) => setF({ ...f, cidade: v })} autoComplete="address-level2" />
            <CampoSelecao busca={false} rotulo="UF" valor={f.uf} aoMudar={(v) => setF({ ...f, uf: v })} vazio="Escolha o estado" opcoes={UFS.map((u) => ({ valor: u, rotulo: u }))} />
            <Campo rotulo="País" valor={f.pais} aoMudar={(v) => setF({ ...f, pais: v })} autoComplete="country-name" />
          </div>
          <span className="mtc-rotulo">Chave PIX pro repasse</span>
          <div className="mtc-grade" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))" }}>
            <CampoSelecao busca={false} rotulo="Tipo da chave" valor={f.pixTipo} aoMudar={(v) => setF({ ...f, pixTipo: v, pixChave: f.pixChave ? formatarChavePix(v, f.pixChave) : "" })} vazio="Escolha o tipo" opcoes={TIPOS_DE_PIX} />
            <Campo
              rotulo="Chave PIX"
              valor={f.pixChave}
              aoMudar={(v) => setF({ ...f, pixChave: tipo ? formatarChavePix(tipo, v) : v })}
              mono={tipo !== "EMAIL"}
              inputMode={chave?.teclado}
              autoCapitalize="none"
              spellCheck={false}
              placeholder={chave?.exemplo}
              dica={tipo ? "É pra onde o repasse vai. Confira com calma." : "Escolha o tipo antes: a chave ganha o formato certo."}
            />
          </div>
          <div><Botao type="submit" carregando={acao.carregando}>Salvar dados</Botao></div>
        </Pilha>
      </form>
    </Cartao>
  );
}

/**
 * E-mails da conta: o principal (onde chegam os avisos e com o qual se entra) e os outros. Adicionar pede o codigo que
 * vai pro e-mail novo; confirmado, da pra torna-lo o principal. As mesmas rotas do backend (`nucleo/perfil/rotas.ts`).
 */
function Emails({ p }: { p: Perfil }) {
  const { postar } = useApi();
  const qc = useQueryClient();
  const pedir = useAcao();
  const confirmar = useAcao();
  const principal = useAcao();
  const [novo, setNovo] = useState("");
  const [enviadoPara, setEnviadoPara] = useState<string | null>(null);
  const [codigo, setCodigo] = useState("");
  const [aviso, setAviso] = useState<string>();
  const recarregar = () => qc.invalidateQueries({ queryKey: ["perfil"] });
  const enviar = async (ev: FormEvent) => {
    ev.preventDefault();
    setAviso(undefined);
    const email = novo.trim();
    const ok = await pedir.rodar(() => postar("/v1/me/emails", { email }));
    if (ok) { setEnviadoPara(email); setCodigo(""); }
  };
  const conferir = async (ev: FormEvent) => {
    ev.preventDefault();
    let r: { email: string } | undefined;
    const ok = await confirmar.rodar(async () => { r = await postar<{ email: string; principal: boolean }>("/v1/me/emails/confirmar", { codigo }); });
    if (!ok) return;
    setAviso(`E-mail ${r?.email ?? enviadoPara} confirmado. Ele já está na sua conta.`);
    setEnviadoPara(null); setNovo(""); setCodigo("");
    await recarregar();
  };
  const tornarPrincipal = async (id: string, email: string) => {
    setAviso(undefined);
    const ok = await principal.rodar(() => postar(`/v1/me/emails/${id}/principal`));
    if (!ok) return;
    setAviso(`Agora o e-mail principal é ${email}. Avisamos o anterior da troca.`);
    await recarregar();
  };
  const ordenados = [...p.emails].sort((a, b) => Number(b.principal) - Number(a.principal));
  return (
    <Cartao>
      <Pilha espaco={4}>
        <div style={{ display: "grid", gap: 2 }}>
          <span className="mtc-rotulo">E-mails</span>
          <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>Você entra com o CPF ou com qualquer e-mail confirmado. Os avisos vão pro principal.</span>
        </div>
        {aviso && <Aviso tipo="sucesso">{aviso}</Aviso>}
        {principal.erro && <Aviso tipo="erro">{principal.erro}</Aviso>}
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: "var(--e-2)" }}>
          {ordenados.map((e) => (
            <li key={e.id} className="mtc-linha mtc-linha--entre" style={{ gap: "var(--e-2)", minHeight: 48 }}>
              <span style={{ display: "flex", gap: "var(--e-2)", alignItems: "center", flexWrap: "wrap", minWidth: 0 }}>
                <span className="mtc-mono" style={{ overflowWrap: "anywhere" }}>{e.email}</span>
                {e.principal && <Selo acento>Principal</Selo>}
                {e.verificado ? <Selo tom="sucesso">Confirmado</Selo> : <Selo tom="alerta">Não confirmado</Selo>}
              </span>
              {!e.principal && e.verificado && <Botao variante="fantasma" tamanho="pequeno" disabled={principal.carregando} onClick={() => void tornarPrincipal(e.id, e.email)}>Tornar principal</Botao>}
            </li>
          ))}
        </ul>
        {!enviadoPara ? (
          <form className="mtc-formulario" onSubmit={enviar} noValidate>
            {pedir.erro && <Aviso tipo="erro">{pedir.erro}</Aviso>}
            <Campo rotulo="Adicionar e-mail" type="email" valor={novo} aoMudar={setNovo} autoComplete="email" autoCapitalize="none" spellCheck={false} placeholder="voce@exemplo.com" dica="Mandamos um código de 6 dígitos pra ele. O e-mail entra na conta quando você confirmar." />
            <div><Botao type="submit" variante="secundario" carregando={pedir.carregando} disabled={!emailValido(novo)}>Enviar código</Botao></div>
          </form>
        ) : (
          <form className="mtc-formulario" onSubmit={conferir} noValidate>
            <Aviso tipo="info">Enviamos um código para <strong className="mtc-mono">{enviadoPara}</strong>. Ele vale por alguns minutos.</Aviso>
            {confirmar.erro && <Aviso tipo="erro">{confirmar.erro}</Aviso>}
            <CampoCodigo rotulo="Código do e-mail" valor={codigo} aoMudar={setCodigo} />
            <div className="mtc-linha" style={{ gap: "var(--e-2)" }}>
              <Botao type="submit" carregando={confirmar.carregando} disabled={codigo.length !== 6}>Confirmar e-mail</Botao>
              <Botao variante="fantasma" onClick={() => { setEnviadoPara(null); setCodigo(""); confirmar.limpar(); }}>Usar outro e-mail</Botao>
            </div>
          </form>
        )}
      </Pilha>
    </Cartao>
  );
}

/**
 * Trocar a senha com a atual (spec 011). O backend encerra as outras sessoes e devolve uma nova pra este aparelho: a
 * area adota a sessao nova e continua aberta aqui.
 */
function TrocarSenha() {
  const { api, entrou } = useSessao();
  const acao = useAcao();
  const [atual, setAtual] = useState("");
  const [nova, setNova] = useState("");
  const [repetida, setRepetida] = useState("");
  const [feito, setFeito] = useState(false);
  const diferentes = repetida.length > 0 && repetida !== nova;
  const trocar = async (ev: FormEvent) => {
    ev.preventDefault();
    setFeito(false);
    const ok = await acao.rodar(async () => {
      const r = await api<RespostaDeEntrada>("/v1/me/senha", { method: "POST", body: JSON.stringify({ senha_atual: atual, nova_senha: nova }) });
      await entrou(r);
    });
    if (!ok) return;
    setAtual(""); setNova(""); setRepetida("");
    setFeito(true);
  };
  return (
    <Cartao>
      <form className="mtc-formulario" onSubmit={trocar} noValidate>
        <Pilha espaco={4}>
          <span className="mtc-rotulo">Trocar senha</span>
          {acao.erro && <Aviso tipo="erro">{acao.erro}</Aviso>}
          {feito && <Aviso tipo="sucesso">Senha trocada. As outras sessões foram encerradas; neste aparelho você continua conectado.</Aviso>}
          <div className="mtc-grade" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))" }}>
            <CampoSenha rotulo="Senha atual" valor={atual} aoMudar={setAtual} autoComplete="current-password" />
            <CampoSenha rotulo="Nova senha" valor={nova} aoMudar={setNova} autoComplete="new-password" dica="Pelo menos 8 caracteres." />
            <CampoSenha rotulo="Repita a nova senha" valor={repetida} aoMudar={setRepetida} autoComplete="new-password" erro={diferentes ? "As duas senhas não são iguais." : undefined} />
          </div>
          <div><Botao type="submit" variante="secundario" carregando={acao.carregando} disabled={!atual || !nova || nova !== repetida}>Trocar senha</Botao></div>
        </Pilha>
      </form>
    </Cartao>
  );
}
